import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

export type StatCardTone = "cyan" | "purple" | "green" | "amber";

const TONES: Record<StatCardTone, string> = {
  cyan: "bg-gradient-to-br from-sky-500 to-sky-600 shadow-glow-cyan dark:from-sky-600 dark:to-sky-700",
  purple:
    "bg-gradient-to-br from-violet-500 to-violet-600 shadow-glow-purple dark:from-violet-600 dark:to-violet-700",
  green:
    "bg-gradient-to-br from-emerald-500 to-emerald-600 dark:from-emerald-600 dark:to-emerald-700",
  amber:
    "bg-gradient-to-br from-amber-500 to-orange-500 dark:from-amber-600 dark:to-orange-600",
};

interface StatCardProps {
  tone: StatCardTone;
  icon: ReactNode;
  title: string;
  subtitle: string;
  /** Optional big number shown above the title. */
  value?: string | number;
  onClick?: () => void;
  className?: string;
}

/**
 * Big colorful stat tile (e.g. "Completed — 2 tasks done today").
 * Renders as a <button> when `onClick` is provided.
 */
export function StatCard({
  tone,
  icon,
  title,
  subtitle,
  value,
  onClick,
  className,
}: StatCardProps) {
  const content = (
    <>
      <span className="flex items-start justify-between">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/25 text-white"
          aria-hidden="true"
        >
          {icon}
        </span>
        {value !== undefined && (
          <span className="text-3xl font-extrabold leading-none tracking-tight text-white">
            {value}
          </span>
        )}
      </span>
      <span className="mt-6 block">
        <span className="block text-base font-bold text-white">{title}</span>
        <span className="block text-[13px] font-medium text-white/90">{subtitle}</span>
      </span>
    </>
  );

  const classes = cn(
    "relative flex flex-col justify-between text-left rounded-3xl p-4 min-h-[128px] overflow-hidden",
    TONES[tone],
    className
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          classes,
          "transition-transform active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        )}
      >
        {content}
      </button>
    );
  }

  return <div className={classes}>{content}</div>;
}

export default StatCard;
